import React from 'react';
import { normalizeArray } from './utils/normalize';

const EnglishTeacherResumePreview = ({ componentRef, resumeData = {}, onImageUpload }) => {
  
  // Extract data from resumeData
  const {
    full_name,
    job_title,
    email,
    phone,
    location,
    address,
    linkedin,
    github,
    leetcode,
    hackerrank,
    professional_summary,
    profile_image_url,
    work_experience,
    relevant_experience,
    education,
    projects,
    skills = {},
    key_skills = [],
    languages,
    interests,
    publications,
    volunteer_work,
    additional_info,
    achievements,
    certifications,
    websites = [],
    personal_website,
    portfolio,
  } = resumeData || {};

  const trimText = (value) => (typeof value === 'string' ? value.trim() : '');
  
  const websiteList = [personal_website, portfolio, ...websites].map(trimText).filter(Boolean);
  
  const hasUserData = Boolean(
    trimText(full_name) ||
    trimText(job_title) ||
    trimText(email) ||
    trimText(phone) ||
    trimText(location) ||
    trimText(address) ||
    trimText(professional_summary) ||
    websiteList.length ||
    (Array.isArray(work_experience) && work_experience.length) ||
    (Array.isArray(relevant_experience) && relevant_experience.length) ||
    (Array.isArray(education) && education.length) ||
    (Array.isArray(projects) && projects.length) ||
    (Array.isArray(achievements) && achievements.length)
  );
  
  const isTemplateResume = resumeData?.isTemplate === false ? false : !hasUserData;

  const pickDisplay = (value, fallback = '') => {
    const trimmed = trimText(value);
    if (trimmed) return trimmed;
    return isTemplateResume ? fallback : '';
  };
  const cleanArray = (value) => {
    if (Array.isArray(value)) return value.map(trimText).filter(Boolean);
    if (typeof value === 'string') return value.split(/\r?\n|[•●▪■\-–;]+/).map(trimText).filter(Boolean);
    return [];
  };
  const formatDateRange = (item = {}) => {
    const start = trimText(item.start_date || item.start_year || '');
    const end = trimText(item.end_date || item.end_year || '');
    if (start && end) return `${start} - ${end}`;
    return start || end || '';
  };

  const displayFullName = pickDisplay(full_name, 'Scarlett Jackson');
  const displayJobTitle = pickDisplay(job_title, 'High School English Teacher');
  const displayEmail = pickDisplay(email, 'support@resumedesign.ai');
  const displayPhone = pickDisplay(phone, '(555) 345-6789');
  const displayLocation = pickDisplay(address || location, 'New York, US');

  const defaultSummaryText = 'Passionate and dedicated High School English Teacher with 13 years of experience in creating engaging lesson plans and fostering a love for literature in students. Proven track record of improving standardized test scores by 20% through innovative teaching methods. Recognized for implementing a successful after-school writing program that resulted in a 30% increase in student participation. Skilled in creating a positive and inclusive classroom environment that promotes critical thinking and creativity. Strong communication skills and ability to collaborate effectively with colleagues to enhance student learning outcomes.';
  const summaryContent = pickDisplay(professional_summary, defaultSummaryText);
  const shouldRenderSummary = Boolean(summaryContent);

  const experiencesSource = Array.isArray(relevant_experience) && relevant_experience.length > 0
    ? relevant_experience
    : Array.isArray(work_experience)
      ? work_experience
      : [];
  const experiences = experiencesSource.map((exp) => ({
    position: trimText(exp.position || exp.title),
    company: trimText(exp.company || exp.employer),
    location: trimText(exp.location),
    dateRange: formatDateRange(exp),
    bullets: cleanArray(exp.description_points || exp.responsibilities || exp.duties || exp.summary || exp.description),
  })).filter((exp) => exp.position || exp.company || exp.location || exp.dateRange || exp.bullets.length > 0);

  const educationList = Array.isArray(education) ? education.map((edu) => ({
    degree: trimText(edu.degree),
    field: trimText(edu.field_of_study),
    institution: trimText(edu.institute || edu.institution),
    location: trimText(edu.location),
    dateRange: formatDateRange(edu) || trimText(edu.graduation_date),
    cgpa: trimText(edu.cgpa || edu.gpa),
    coursework: cleanArray(edu.coursework),
    description: trimText(edu.description),
  })) : [];

  const projectsList = Array.isArray(projects) ? projects.map((project) => ({
    title: trimText(project.title || project.name),
    link: trimText(project.github_link || project.link),
    year: trimText(project.year),
    description: cleanArray(project.description),
    technologies: cleanArray(project.technologies || project.tech_stack),
  })).filter((proj) => proj.title || proj.link || proj.year || proj.description.length > 0 || proj.technologies.length > 0) : [];

  const achievementsList = cleanArray(achievements);
  const certificationsList = Array.isArray(certifications) ? certifications : [];
  const languagesList = Array.isArray(languages) ? languages : [];
  const interestsList = cleanArray(interests);
  const publicationsList = cleanArray(publications);
  const volunteerList = cleanArray(volunteer_work);
  const additionalInfoList = cleanArray(additional_info);

  const aggregatedSkills = Array.from(new Set([
    ...cleanArray(key_skills),
    ...Object.values(skills || {}).flatMap((list) => cleanArray(list)),
  ])).filter(Boolean);

  const defaultSkills = [
    'Literature',
    'Writing',
    'Grammar',
    'Communication',
    'Classroom management',
    'Lesson planning',
    'Critical thinking',
    'Analytical reading',
    'Public speaking',
    'Team collaboration',
  ];
  const displaySkills = aggregatedSkills.length > 0 ? aggregatedSkills : (isTemplateResume ? defaultSkills : []);

  const contactItems = [
    { icon: '✉️', text: displayEmail, href: trimText(email) ? `mailto:${trimText(email)}` : '' },
    { icon: '📞', text: displayPhone, href: trimText(phone) ? `tel:${trimText(phone)}` : '' },
    { icon: '🏠', text: displayLocation },
    ...websiteList.map((site) => ({ icon: '🌐', text: site, href: site })),
  ].filter((item) => trimText(item.text));

  const socialLinks = [
    { icon: '💼', text: 'LinkedIn', href: trimText(linkedin) },
    { icon: '💻', text: 'GitHub', href: trimText(github) },
    { icon: '🎯', text: 'LeetCode', href: trimText(leetcode) },
    { icon: '🏆', text: 'HackerRank', href: trimText(hackerrank) },
  ].filter((link) => link.href);

  const canUploadImage = !isTemplateResume && typeof onImageUpload === 'function';

  const renderSkillBar = (skill, level = 9) => {
    const segments = 5;
    const filledSegments = Math.ceil((level / 10) * segments);
    
    return (
      <div className="skill-bar-item">
        <div className="skill-bar-label">{skill}</div>
        <div className="skill-bar-container">
          {Array.from({ length: segments }, (_, index) => (
            <div 
              key={index} 
              className={`skill-segment ${index < filledSegments ? 'filled' : ''}`}
            ></div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div id="section-to-print" ref={componentRef} className="english-teacher-resume-preview tech-resume-preview">
      {/* Top Header Bar */}
      <div className="tech-header-bar english-header-bar">
        <div className="header-left">
          {displayFullName && <h1 className="header-name">{displayFullName}</h1>}
          {displayJobTitle && <p className="header-title">{displayJobTitle}</p>}
        </div>
        <div className="header-right">
          {profile_image_url ? (
            <div
              className="header-profile-image"
              onClick={() => {
                if (!canUploadImage) return;
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = 'image/*';
                fileInput.onchange = (e) => {
                  const file = e.target.files?.[0];
                  if (file) onImageUpload(file);
                };
                fileInput.click();
              }}
              style={{ cursor: canUploadImage ? 'pointer' : 'default', transition: 'all 0.3s ease' }}
            >
              <img
                src={profile_image_url}
                alt="Profile"
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            </div>
          ) : canUploadImage ? (
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
              onClick={() => {
                const fileInput = document.createElement('input');
                fileInput.type = 'file';
                fileInput.accept = 'image/*';
                fileInput.onchange = (e) => {
                  const file = e.target.files?.[0];
                  if (file) onImageUpload(file);
                };
                fileInput.click();
              }}
            >
              <div
                style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  backgroundColor: '#FFFFFF',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '8px',
                  overflow: 'hidden',
                }}
              >
                <svg width="50" height="50" viewBox="0 0 24 24" fill="none" style={{ color: '#34495e' }}>
                  <path
                    d="M12 12C14.21 12 16 10.21 16 8C16 5.79 14.21 4 12 4C9.79 4 8 5.79 8 8C8 10.21 9.79 12 12 12ZM12 14C9.33 14 4 15.34 4 18V20H20V18C20 15.34 14.67 14 12 14Z"
                    fill="currentColor"
                  />
                </svg>
              </div>
              <span style={{ fontSize: '8px', fontWeight: 'bold', lineHeight: '1.2' }}>Upload your image</span>
            </div>
          ) : isTemplateResume ? (
            <div className="header-profile-placeholder">
              <img src="./english.jpeg" alt="Profile" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            </div>
          ) : null}
        </div>
      </div>

      <div className="tech-resume-container">
        {/* Left Sidebar */}
        <div className="tech-sidebar">
          <div className="tech-section">
            <h3 className="tech-section-title">Contact Details</h3>
            {contactItems.map((item, idx) => (
              <div className="tech-contact-item" key={`contact-${idx}`}>
                <span className="contact-icon">{item.icon}</span>
                {item.href ? (
                  <a href={item.href} target="_blank" rel="noreferrer" className="contact-text">
                    {item.text}
                  </a>
                ) : (
                  <span className="contact-text">{item.text}</span>
                )}
              </div>
            ))}
          </div>

          {socialLinks.length > 0 && (
            <div className="tech-section">
              <h3 className="tech-section-title">Social Links</h3>
              {socialLinks.map((link) => (
                <div className="tech-contact-item" key={link.text}>
                  <span className="contact-icon">{link.icon}</span>
                  <a href={link.href} target="_blank" rel="noreferrer" className="contact-text">
                    {link.text}
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Technical Skills with Bars */}
          {displaySkills.length > 0 && (
            <div className="tech-section">
              <h3 className="tech-section-title">Skills</h3>
              <div className="tech-skills-bars">
                {displaySkills.map((skill, index) => renderSkillBar(skill, 8 + (index % 3)))}
              </div>
            </div>
          )}

          {/* Soft Skills with Dots */}
          {/* Removed - only show if user has soft skills data */}

          {/* Languages */}
          {/* Removed - only show if user has languages data */}
        </div>

        {/* Right Content - White */}
        <div className="tech-content">
          {shouldRenderSummary && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Professional Summary</h3>
              <p className="tech-summary">{summaryContent}</p>
            </div>
          )}

          {experiences.length > 0 ? (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Work Experience</h3>
              <div className="tech-experience-list">
                {experiences.map((exp, index) => (
                  <div key={index} className="tech-experience-item">
                    <div className="tech-exp-header">
                      <div className="tech-exp-left">
                        {exp.position && <h4 className="tech-exp-position">{exp.position}</h4>}
                        {exp.company && <p className="tech-exp-company">{exp.company}</p>}
                        {exp.location && <p className="tech-exp-location">{exp.location}</p>}
                      </div>
                      {exp.dateRange && <div className="tech-exp-dates">{exp.dateRange}</div>}
                    </div>
                    {exp.bullets.length > 0 && (
                      <ul className="tech-exp-responsibilities">
                        {exp.bullets.map((resp, rIndex) => (
                          <li key={rIndex}>{resp}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : isTemplateResume ? (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Work Experience</h3>
              <div className="tech-experience-list">
                <div className="tech-experience-item">
                  <div className="tech-exp-header">
                    <div className="tech-exp-left">
                      <h4 className="tech-exp-position">High School English Teacher</h4>
                      <p className="tech-exp-company">ABC High School</p>
                    </div>
                    <div className="tech-exp-dates">Aug 2008 - Jun 2021</div>
                  </div>
                  <ul className="tech-exp-responsibilities">
                    <li>Planned and delivered engaging English lessons to high school students</li>
                    <li>Provided constructive feedback on student assignments to support academic growth</li>
                    <li>Organized and supervised extracurricular activities such as book clubs and writing competitions</li>
                    <li>Collaborated with colleagues to develop curriculum and assessment strategies</li>
                    <li>Achieved a 95% pass rate for students in the annual English standardized test</li>
                  </ul>
                </div>
                <div className="tech-experience-item">
                  <div className="tech-exp-header">
                    <div className="tech-exp-left">
                      <h4 className="tech-exp-position">Lead High School English Teacher</h4>
                      <p className="tech-exp-company">XYZ High School</p>
                    </div>
                    <div className="tech-exp-dates">Aug 2014 - Present</div>
                  </div>
                  <ul className="tech-exp-responsibilities">
                    <li>Mentored and coached junior English teachers to improve teaching practices</li>
                    <li>Implemented technology tools to enhance student learning experiences in the classroom</li>
                    <li>Developed differentiated instruction techniques to meet the diverse needs of students</li>
                    <li>Led professional development workshops for faculty on literacy strategies</li>
                    <li>Increased student participation in English literature events by 20% through innovative programming</li>
                  </ul>
                </div>
              </div>
            </div>
          ) : null}

          {educationList.length > 0 ? (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Education</h3>
              <div className="tech-education-list">
                {educationList.map((edu, index) => (
                  <div key={index} className="tech-education-item">
                    <div className="tech-edu-header">
                      <div className="tech-edu-left">
                        {edu.degree && <h4 className="tech-edu-degree">{edu.degree}</h4>}
                        {(edu.institution || edu.location) && (
                          <p className="tech-edu-school">
                            {[edu.institution, edu.location].filter(Boolean).join(', ')}
                          </p>
                        )}
                      </div>
                      {edu.dateRange && <div className="tech-edu-dates">{edu.dateRange}</div>}
                    </div>
                    {edu.field && (
                      <p className="tech-edu-description">Field of Study: {edu.field}</p>
                    )}
                    {edu.cgpa && (
                      <p className="tech-edu-description">CGPA: {edu.cgpa}</p>
                    )}
                    {edu.coursework.length > 0 && (
                      <p className="tech-edu-description">
                        Relevant Coursework: {edu.coursework.join(', ')}
                      </p>
                    )}
                    {edu.description && (
                      <p className="tech-edu-description">{edu.description}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : isTemplateResume ? (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Education</h3>
              <div className="tech-education-list">
                <div className="tech-education-item">
                  <div className="tech-edu-header">
                    <div className="tech-edu-left">
                      <h4 className="tech-edu-degree">Masters in Education</h4>
                      <p className="tech-edu-school">University of Education</p>
                    </div>
                    <div className="tech-edu-dates">Jan 2009 - Jan 2011</div>
                  </div>
                  <p className="tech-edu-description">Focused on advanced teaching techniques and curriculum development.</p>
                </div>
                <div className="tech-education-item">
                  <div className="tech-edu-header">
                    <div className="tech-edu-left">
                      <h4 className="tech-edu-degree">Bachelors in English Literature</h4>
                      <p className="tech-edu-school">State College</p>
                    </div>
                    <div className="tech-edu-dates">Jan 2005 - Jan 2009</div>
                  </div>
                  <p className="tech-edu-description">Studied various genres of literature and honed writing skills.</p>
                </div>
              </div>
            </div>
          ) : null}

          {projectsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Projects</h3>
              <div className="tech-projects-list">
                {projectsList.map((project, index) => (
                  <div key={index} className="tech-project-item">
                    <div className="tech-project-header">
                      <div className="tech-project-left">
                        {project.title && <h4 className="tech-project-title">{project.title}</h4>}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          {project.year && (
                            <span
                              style={{
                                fontSize: '0.875rem',
                                color: '#666',
                                fontStyle: 'italic',
                              }}
                            >
                              {project.year}
                            </span>
                          )}
                          {project.link && (
                            <a href={project.link} target="_blank" rel="noopener noreferrer" className="tech-project-link">
                              View Project
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                    {project.description.length > 0 && (
                      <ul className="tech-project-description">
                        {project.description.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {project.technologies.length > 0 && (
                      <div className="tech-project-tech">Technologies: {project.technologies.join(', ')}</div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {achievementsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Achievements</h3>
              <ul className="tech-achievements-list">
                {achievementsList.map((achievement, index) => (
                  <li key={index}>{achievement}</li>
                ))}
              </ul>
            </div>
          )}

          {certificationsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Certifications</h3>
              <div className="tech-certifications-list">
                {certificationsList.map((cert, index) => {
                  const isObject = typeof cert === 'object' && cert !== null;
                  const name = isObject ? trimText(cert.name || cert.title) : trimText(cert);
                  if (!name) return null;
                  const provider = isObject ? trimText(cert.provider) : '';
                  const date = isObject ? trimText(cert.date || cert.year || cert.issued_on || cert.issued) : '';
                  return (
                    <div key={index} className="tech-certification-item">
                      <div className="tech-cert-name">{name}</div>
                      {provider && <div className="tech-cert-provider">{provider}</div>}
                      {date && <div className="tech-cert-date">{date}</div>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {languagesList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Languages</h3>
              <ul className="tech-achievements-list">
                {languagesList.map((lang, index) => {
                  if (typeof lang === 'string') {
                    const text = trimText(lang);
                    return text ? <li key={index}>{text}</li> : null;
                  }
                  const languageName = trimText(lang.language || lang.name);
                  const proficiencyLevel = trimText(lang.proficiency);
                  if (!languageName) return null;
                  return (
                    <li key={index}>
                      {languageName}
                      {proficiencyLevel ? ` (${proficiencyLevel})` : ''}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}

          {interestsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Interests</h3>
              <p className="tech-summary">{interestsList.join(', ')}</p>
            </div>
          )}

          {publicationsList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Publications</h3>
              <ul className="tech-achievements-list">
                {publicationsList.map((pub, index) => (
                  <li key={index}>{pub}</li>
                ))}
              </ul>
            </div>
          )}

          {volunteerList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Volunteer Work</h3>
              <ul className="tech-achievements-list">
                {volunteerList.map((vol, index) => (
                  <li key={index}>{vol}</li>
                ))}
              </ul>
            </div>
          )}

          {additionalInfoList.length > 0 && (
            <div className="tech-content-section">
              <h3 className="tech-content-title">Additional Information</h3>
              <ul className="tech-achievements-list">
                {additionalInfoList.map((info, index) => (
                  <li key={index}>{info}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>



      <style jsx>{`
        .tech-resume-preview {
          width: 100%;
          max-width: 794px;
          min-height: auto; /* Remove fixed height - let content determine height */
          margin: 0 auto;
          background: white;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
          overflow: visible; /* Allow content to grow naturally */
          position: relative;
          display: flex;
          flex-direction: column;
        }

        .tech-header-bar {
          background: #4a5568;
          color: white;
          height: 140px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 40px;
          box-sizing: border-box;
        }

        .header-left {
          flex: 1;
        }

        .header-name {
          font-size: 42px;
          font-weight: bold;
          margin: 0 0 5px 0;
          color: white;
          line-height: 1.1;
        }

        .header-title {
          font-size: 18px;
          color: #d69e2e;
          margin: 0;
          font-weight: 500;
        }

        .header-right {
          margin-left: 40px;
        }

        .header-profile-image,
        .header-profile-placeholder {
          width: 100px;
          height: 100px;
          border-radius: 50%;
          overflow: hidden;
          border: 4px solid white;
        }

        .header-profile-image img,
        .header-profile-placeholder img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .header-profile-image:hover {
          transform: scale(1.05);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }

        .tech-resume-container {
          display: flex;
          flex: 1;
          width: 100%;
          min-height: calc(100vh - 140px); /* Use viewport height instead of fixed */
          padding: 0;
          margin: 0;
          overflow: visible; /* Allow natural growth */
          align-items: stretch; /* Ensure both columns grow to same height */
        }

        .tech-sidebar {
          width: 35%;
          background: #f7fafc;
          color: #2d3748;
          padding: 25px 20px;
          box-sizing: border-box;
          min-height: fit-content; /* Remove fixed height - grow with content */
          overflow: visible; /* Allow natural growth */
          border-right: 1px solid #e2e8f0;
          display: flex;
          flex-direction: column;
        }

        .tech-content {
          width: 65%;
          background: white;
          padding: 25px 30px;
          box-sizing: border-box;
          min-height: fit-content; /* Remove fixed height - grow with content */
          overflow: visible; /* Allow natural growth */
          display: flex;
          flex-direction: column;
        }

        .tech-section {
          
        }

        .tech-section-title {
          font-size: 16px;
          font-weight: bold;
          margin-bottom: 12px;
          color: #2d3748;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 6px;
        }

        .tech-contact-item {
          display: flex;
          align-items: center;
          margin-bottom: 10px;
          font-size: 12px;
        }

        .contact-icon {
          margin-right: 12px;
          font-size: 16px;
          width: 20px;
          color: #4a5568;
        }

        .contact-text {
          color: #4a5568;
          word-break: break-all;
        }

        .tech-skills-bars {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }

        .skill-bar-item {
          display: flex;
          flex-direction: column;
          margin-bottom: 8px;
        }

        .skill-bar-label {
          color: #2d3748;
          font-weight: 500;
          font-size: 12px;
          margin-bottom: 4px;
        }

        .skill-bar-container {
          display: flex;
          gap: 2px;
          height: 8px;
          background: transparent;
        }

        .skill-segment {
  flex: 1;
  height: 100%;
  background: #e2e8f0;
  clip-path: polygon(
    4px 0,
    100% 0,
    calc(100% - 4px) 100%,
    0 100%
  );
}

/* Filled state with gradient */
.skill-segment.filled {
  background: linear-gradient(90deg, #d69e2e, #b7791f);
  clip-path: polygon(
    4px 0,
    100% 0,
    calc(100% - 4px) 100%,
    0 100%
  );
}


        .soft-skills-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .soft-skill-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 12px;
          margin-bottom: 6px;
        }

        .soft-skill-name {
          color: #2d3748;
          font-weight: 500;
        }

        .skill-level {
          display: flex;
          gap: 3px;
        }

        .star {
          font-size: 12px;
          color: #e2e8f0;
        }

        .star.filled {
          color: #d69e2e;
        }

        .languages-list {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }

        .language-tag {
          background: #d69e2e;
          color: white;
          padding: 5px 12px;
          border-radius: 15px;
          font-size: 12px;
          font-weight: 500;
        }

        .tech-content-section {
          
        }

        .tech-content-title {
          font-size: 18px;
          font-weight: bold;
          color: #2d3748;
          margin-bottom: 15px;
          border-bottom: 2px solid #e2e8f0;
          padding-bottom: 5px;
        }

        .tech-summary {
          font-size: 13px;
          line-height: 1.6;
          color: #4a5568;
          margin: 0;
          text-align: justify;
        }

        .tech-experience-list,
        .tech-projects-list,
        .tech-education-list {
          display: flex;
          flex-direction: column;
         
        }

        .tech-experience-item,
        .tech-project-item,
        .tech-education-item {
          margin-bottom: 15px;
        }

        .tech-exp-header,
        .tech-edu-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 10px;
        }

        .tech-exp-left,
        .tech-edu-left {
          flex: 1;
        }

        .tech-exp-position,
        .tech-project-title,
        .tech-edu-degree {
          font-size: 15px;
          font-weight: bold;
          color: #d69e2e;
          margin: 0 0 4px 0;
        }

        .tech-exp-company,
        .tech-edu-school {
          font-size: 13px;
          color: #4a5568;
          margin: 0;
        }

        .tech-exp-dates,
        .tech-edu-dates {
          font-size: 12px;
          color: #718096;
          font-weight: 600;
          text-align: right;
          white-space: nowrap;
        }

        .tech-exp-responsibilities {
          list-style: disc;
          margin: 10px 0 0 20px;
          padding: 0;
          list-style-position: outside;
        }

        .tech-exp-responsibilities li {
          font-size: 12px;
          line-height: 1.5;
          color: #4a5568;
          margin-bottom: 4px;
          padding-left: 0;
          margin-left: 0;
          display: list-item;
          list-style-type: disc;
          list-style-position: outside;
        }

        .tech-edu-description {
          font-size: 12px;
          line-height: 1.5;
          color: #4a5568;
          margin: 8px 0 0 0;
        }

        .tech-project-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 8px;
        }

        .tech-project-link {
          font-size: 12px;
          color: #3b82f6;
          text-decoration: none;
        }

        .tech-project-description {
          list-style: disc;
          margin: 8px 0 0 20px;
          padding: 0;
        }

        .tech-project-description li {
          font-size: 13px;
          line-height: 1.5;
          color: #4a5568;
          margin-bottom: 4px;
        }

        .tech-project-tech {
          font-size: 12px;
          color: #4a5568;
          margin: 8px 0 0 0;
        }

        .tech-achievements-list {
          list-style: disc;
          padding-left: 20px;
          margin: 0;
        }

        .tech-achievements-list li {
          font-size: 13px;
          line-height: 1.5;
          color: #4a5568;
          margin-bottom: 4px;
        }

        .tech-certifications-list {
          margin-top: 10px;
        }

        .tech-certification-item {
          margin-bottom: 8px;
          padding-bottom: 8px;
          border-bottom: 1px solid #e5e7eb;
        }

        .tech-certification-item:last-child {
          border-bottom: none;
        }

        .tech-cert-name {
          font-weight: 600;
          color: #1f2937;
          font-size: 11pt;
          margin-bottom: 2px;
        }

        .tech-cert-provider {
          color: #6b7280;
          font-size: 9pt;
          font-style: italic;
        }

        .tech-cert-date {
          color: #6b7280;
          font-size: 9pt;
          margin-top: 2px;
        }

        @media print {
          @page {
            size: A4;
            margin: 0;
            background: white;
          }
          
          html, body {
            background: white !important;
            margin: 0;
            padding: 0;
          }
          
          #section-to-print {
            background: white !important;
          }
          
          .tech-resume-preview {
            box-shadow: none;
            width: 210mm;
            min-height: auto; /* Allow natural height growth for print */
            background: white !important;
          }
          
          .tech-resume-container {
            background: white !important;
          }
          
          .tech-content {
            background: white !important;
          }
        }
      `}</style>
    </div>
  );
};

export default EnglishTeacherResumePreview;
                      
